import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2, MapPin, ShieldAlert, Loader2, RefreshCw, Gauge, Gift, Repeat, Trophy } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { VenueSwitcher } from "@/components/VenueSwitcher";
import { useVenue } from "@/context/VenueContext";

const MAP_W = 520, MAP_H = 360;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (u) => (u ? `${BACKEND}${u}` : null);

export default function PlusAdmin() {
  const navigate = useNavigate();
  const { currentVenue } = useVenue();
  const [settings, setSettings] = useState(null);
  const [zones, setZones] = useState([]);
  const [firewall, setFirewall] = useState(null);
  const [cvDash, setCvDash] = useState(null);
  const [raffles, setRaffles] = useState([]);
  const [raffleForm, setRaffleForm] = useState({ nome: "", min_valid_visits: 1, num_winners: 1, bonus_stars: 0, days: 60 });
  const [rewards, setRewards] = useState([]);
  const [rewardForm, setRewardForm] = useState({ nome: "", descrizione: "", points_required: 100, image_url: "", stock_tracking: false, stock_quantity: 0 });
  const [uploadingImg, setUploadingImg] = useState(false);
  const rewardFileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const drag = useRef(null);
  const svgRef = useRef(null);

  const load = useCallback(async () => {
    const [s, z, f, d, r, rw] = await Promise.all([
      api.get("/plus/settings"), api.get("/plus/zones"), api.get("/plus/firewall-info"),
      api.get("/plus/dashboard/customer-value"), api.get("/plus/raffles"), api.get("/plus/rewards?all=true"),
    ]);
    setSettings(s.data); setZones(z.data); setFirewall(f.data); setCvDash(d.data); setRaffles(r.data); setRewards(rw.data);
  }, []);
  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);
  useEffect(() => {
    const onVenue = () => { load().catch((e) => toast.error(apiError(e))); };
    window.addEventListener("venue-changed", onVenue);
    return () => window.removeEventListener("venue-changed", onVenue);
  }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const body = {
        nome_programma: settings.nome_programma,
        stella_min_visit_minutes: Number(settings.stella_min_visit_minutes),
        stella_max_presence_stars_per_day: Number(settings.stella_max_presence_stars_per_day),
        stella_presence_thresholds: settings.stella_presence_thresholds,
        cv_weights: settings.cv_weights,
        cv_classes: settings.cv_classes,
        cv_period_days: Number(settings.cv_period_days),
        cv_operator_can_view: settings.cv_operator_can_view,
        freq_bonus_enabled: settings.freq_bonus_enabled,
        freq_weekly_visits: Number(settings.freq_weekly_visits),
        freq_weekly_stars: Number(settings.freq_weekly_stars),
        freq_monthly_visits: Number(settings.freq_monthly_visits),
        freq_monthly_stars: Number(settings.freq_monthly_stars),
        freq_max_stars_per_month: Number(settings.freq_max_stars_per_month),
        birthday_bonus_enabled: settings.birthday_bonus_enabled,
        birthday_stars: Number(settings.birthday_stars),
        module_sessions_enabled: settings.module_sessions_enabled,
        module_zones_enabled: settings.module_zones_enabled,
        module_surveys_enabled: settings.module_surveys_enabled,
        module_customer_value_enabled: settings.module_customer_value_enabled,
      };
      await api.patch("/plus/settings", body);
      toast.success("Impostazioni salvate");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const recomputeCV = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/plus/customer-value/recompute", {});
      toast.success(`Customer Value ricalcolato per ${data.recomputed} clienti`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const createRaffle = async (e) => {
    e.preventDefault();
    if (!raffleForm.nome.trim()) { toast.error("Inserisci un nome"); return; }
    if (Number(raffleForm.num_winners) < 1 || Number(raffleForm.days) < 1) { toast.error("Vincitori e periodo devono essere ≥ 1"); return; }
    setSaving(true);
    try {
      const end = new Date();
      const start = new Date(Date.now() - Number(raffleForm.days) * 86400000);
      const { data } = await api.post("/plus/raffles", {
        nome: raffleForm.nome, period_start: start.toISOString(), period_end: end.toISOString(),
        min_valid_visits: Number(raffleForm.min_valid_visits), num_winners: Number(raffleForm.num_winners),
        bonus_stars: Number(raffleForm.bonus_stars),
      });
      toast.success(`Estrazione creata · ${data.participants.length} partecipanti idonei`);
      setRaffleForm({ nome: "", min_valid_visits: 1, num_winners: 1, bonus_stars: 0, days: 60 });
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const drawRaffle = async (rid) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/plus/raffles/${rid}/draw`, {});
      toast.success(`Estrazione effettuata · ${data.winners.length} vincitori`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const addZone = async () => {
    try {
      const { data } = await api.post("/plus/zones", {
        nome: "Nuova zona", colore: "#a855f7", x: 60, y: 60, w: 160, h: 110, ordine: zones.length,
      });
      setZones((z) => [...z, data]);
    } catch (e) { toast.error(apiError(e)); }
  };
  const delZone = async (id) => {
    try { await api.delete(`/plus/zones/${id}`); setZones((z) => z.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const patchZone = async (zone) => {
    try {
      await api.patch(`/plus/zones/${zone.id}`, {
        nome: zone.nome, descrizione: zone.descrizione, colore: zone.colore,
        counts_for_presence: zone.counts_for_presence !== false,
        x: zone.x, y: zone.y, w: zone.w, h: zone.h, ordine: zone.ordine,
      });
    } catch (e) { toast.error(apiError(e)); }
  };

  const onPointerDown = (zone) => (e) => {
    const pt = svgRef.current.getBoundingClientRect();
    drag.current = { id: zone.id, offX: e.clientX - pt.left - zone.x, offY: e.clientY - pt.top - zone.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const pt = svgRef.current.getBoundingClientRect();
    setZones((zs) => zs.map((z) => {
      if (z.id !== drag.current.id) return z;
      let x = Math.max(0, Math.min(e.clientX - pt.left - drag.current.offX, MAP_W - z.w));
      let y = Math.max(0, Math.min(e.clientY - pt.top - drag.current.offY, MAP_H - z.h));
      return { ...z, x: Math.round(x), y: Math.round(y) };
    }));
  };
  const onPointerUp = () => {
    if (drag.current) {
      const z = zones.find((x) => x.id === drag.current.id);
      if (z) patchZone(z);
      drag.current = null;
    }
  };

  const updZoneField = (id, k, v) => setZones((zs) => zs.map((z) => z.id === id ? { ...z, [k]: v } : z));

  const uploadRewardImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/plus/rewards/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRewardForm((s) => ({ ...s, image_url: data.url }));
      toast.success("Immagine caricata");
    } catch (e2) { toast.error(apiError(e2)); } finally { setUploadingImg(false); }
  };

  const createReward = async (e) => {
    e.preventDefault();
    if (!rewardForm.nome.trim()) { toast.error("Inserisci un nome"); return; }
    if (Number(rewardForm.points_required) <= 0) { toast.error("Costo in punti non valido"); return; }
    setSaving(true);
    try {
      await api.post("/plus/rewards", {
        nome: rewardForm.nome, descrizione: rewardForm.descrizione || null,
        points_required: Number(rewardForm.points_required), image_url: rewardForm.image_url || null,
        stock_tracking: rewardForm.stock_tracking, stock_quantity: Number(rewardForm.stock_quantity) || 0,
        attivo: true, ordine: rewards.length,
      });
      toast.success("Premio aggiunto al catalogo");
      setRewardForm({ nome: "", descrizione: "", points_required: 100, image_url: "", stock_tracking: false, stock_quantity: 0 });
      await load();
    } catch (e2) { toast.error(apiError(e2)); } finally { setSaving(false); }
  };

  const patchReward = async (id, updates) => {
    try {
      await api.patch(`/plus/rewards/${id}`, updates);
      setRewards((rs) => rs.map((r) => r.id === id ? { ...r, ...updates } : r));
      toast.success("Premio aggiornato");
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteReward = async (id) => {
    const r = rewards.find((x) => x.id === id);
    if (!window.confirm(`Eliminare il premio "${r?.nome || ""}" dal catalogo?`)) return;
    try { await api.delete(`/plus/rewards/${id}`); setRewards((rs) => rs.filter((x) => x.id !== id)); toast.success("Premio rimosso"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!settings) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500">...</div>;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <button data-testid="plus-admin-back" onClick={() => navigate("/")} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-5 h-5" /> Home PLUS
        </button>
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <h1 className="font-display text-3xl font-bold">Admin · PUNTO STELLA <span className="text-sky-400">PLUS</span></h1>
          <VenueSwitcher />
        </div>

        {/* Firewall */}
        <div className="rounded-3xl bg-red-500/5 border border-red-500/30 p-6 mb-6">
          <div className="flex items-center gap-2 mb-2 text-red-400"><ShieldAlert className="w-5 h-5" /><h2 className="font-display font-bold text-lg">Firewall anti-gioco (tecnico)</h2></div>
          <p className="text-sm text-zinc-400 mb-3">Le seguenti variabili di gioco sono <b>escluse per codice</b> dallo Stella Engine e non possono generare Punti Stella:</p>
          <div className="flex flex-wrap gap-2">
            {firewall?.forbidden_stella_inputs.map((v) => (
              <span key={v} className="text-xs px-2 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300">{v}</span>
            ))}
          </div>
        </div>

        {/* Stella Engine settings */}
        <div className="rounded-3xl bg-zinc-900 border border-zinc-800 p-6 mb-6">
          <h2 className="font-display font-bold text-lg mb-4">Stella Engine · Regole permanenza</h2>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Durata minima visita valida (min)</label>
              <input data-testid="plus-set-minvisit" type="number" value={settings.stella_min_visit_minutes}
                onChange={(e) => setSettings((s) => ({ ...s, stella_min_visit_minutes: e.target.value }))}
                className="w-full h-12 px-4 rounded-xl bg-zinc-800 border border-zinc-700 focus:outline-none focus:border-sky-400" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Max Stelle permanenza / giorno</label>
              <input data-testid="plus-set-dailycap" type="number" value={settings.stella_max_presence_stars_per_day}
                onChange={(e) => setSettings((s) => ({ ...s, stella_max_presence_stars_per_day: e.target.value }))}
                className="w-full h-12 px-4 rounded-xl bg-zinc-800 border border-zinc-700 focus:outline-none focus:border-sky-400" />
            </div>
          </div>
          <label className="block text-sm text-zinc-400 mb-2">Soglie permanenza → Stelle (cumulative)</label>
          <div className="space-y-2 mb-2">
            {(settings.stella_presence_thresholds || []).map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="number" value={t.minutes} data-testid={`plus-thr-min-${i}`}
                  onChange={(e) => setSettings((s) => { const a = [...s.stella_presence_thresholds]; a[i] = { ...a[i], minutes: Number(e.target.value) }; return { ...s, stella_presence_thresholds: a }; })}
                  className="w-24 h-11 px-3 rounded-xl bg-zinc-800 border border-zinc-700" />
                <span className="text-zinc-500 text-sm">min →</span>
                <input type="number" value={t.stars} data-testid={`plus-thr-star-${i}`}
                  onChange={(e) => setSettings((s) => { const a = [...s.stella_presence_thresholds]; a[i] = { ...a[i], stars: Number(e.target.value) }; return { ...s, stella_presence_thresholds: a }; })}
                  className="w-20 h-11 px-3 rounded-xl bg-zinc-800 border border-zinc-700" />
                <span className="text-zinc-500 text-sm">⭐</span>
                <button onClick={() => setSettings((s) => ({ ...s, stella_presence_thresholds: s.stella_presence_thresholds.filter((_, j) => j !== i) }))}
                  className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <button data-testid="plus-thr-add" onClick={() => setSettings((s) => ({ ...s, stella_presence_thresholds: [...(s.stella_presence_thresholds || []), { minutes: 0, stars: 0 }] }))}
            className="text-sky-400 text-sm flex items-center gap-1 mb-4"><Plus className="w-4 h-4" /> Aggiungi soglia</button>

          <div className="grid grid-cols-2 gap-3">
            {["module_sessions_enabled", "module_zones_enabled", "module_surveys_enabled", "module_customer_value_enabled"].map((m) => (
              <label key={m} className="flex items-center gap-3 text-sm text-zinc-300">
                <input type="checkbox" data-testid={`plus-mod-${m}`} checked={!!settings[m]} onChange={(e) => setSettings((s) => ({ ...s, [m]: e.target.checked }))} className="w-5 h-5 accent-sky-500" />
                {m.replace("module_", "Modulo ").replace("_enabled", "").replace("_", " ")}
              </label>
            ))}
          </div>

          <button data-testid="plus-save-settings" onClick={saveSettings} disabled={saving}
            className="mt-5 flex items-center gap-2 px-6 h-12 rounded-2xl bg-sky-500 hover:bg-sky-400 font-bold transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Salva impostazioni
          </button>
        </div>

        {/* Bonus Frequenza + Regalo Compleanno (Stella Engine) */}
        <div className="rounded-3xl bg-zinc-900 border border-zinc-800 p-6 mb-6">
          <h2 className="font-display font-bold text-lg mb-4">Bonus Stelle — Frequenza & Compleanno</h2>
          <p className="text-zinc-500 text-xs mb-4">Basati su presenza/visite valide. Nessun legame col gioco.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3 text-amber-300"><Repeat className="w-5 h-5" /><span className="font-semibold">Bonus Frequenza</span>
                <label className="ml-auto flex items-center gap-2 text-xs text-zinc-400"><input type="checkbox" data-testid="plus-freq-enabled" checked={!!settings.freq_bonus_enabled} onChange={(e) => setSettings((s) => ({ ...s, freq_bonus_enabled: e.target.checked }))} className="w-4 h-4 accent-amber-500" /> attivo</label>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <label className="text-zinc-500 text-xs">Visite/settimana<input type="number" data-testid="plus-freq-wv" value={settings.freq_weekly_visits} onChange={(e) => setSettings((s) => ({ ...s, freq_weekly_visits: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700" /></label>
                <label className="text-zinc-500 text-xs">⭐ settimana<input type="number" data-testid="plus-freq-ws" value={settings.freq_weekly_stars} onChange={(e) => setSettings((s) => ({ ...s, freq_weekly_stars: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700" /></label>
                <label className="text-zinc-500 text-xs">Visite/mese<input type="number" data-testid="plus-freq-mv" value={settings.freq_monthly_visits} onChange={(e) => setSettings((s) => ({ ...s, freq_monthly_visits: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700" /></label>
                <label className="text-zinc-500 text-xs">⭐ mese<input type="number" data-testid="plus-freq-ms" value={settings.freq_monthly_stars} onChange={(e) => setSettings((s) => ({ ...s, freq_monthly_stars: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700" /></label>
                <label className="text-zinc-500 text-xs col-span-2">Tetto ⭐ frequenza / mese<input type="number" data-testid="plus-freq-cap" value={settings.freq_max_stars_per_month} onChange={(e) => setSettings((s) => ({ ...s, freq_max_stars_per_month: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700" /></label>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3 text-pink-300"><Gift className="w-5 h-5" /><span className="font-semibold">Regalo di Compleanno</span>
                <label className="ml-auto flex items-center gap-2 text-xs text-zinc-400"><input type="checkbox" data-testid="plus-bday-enabled" checked={!!settings.birthday_bonus_enabled} onChange={(e) => setSettings((s) => ({ ...s, birthday_bonus_enabled: e.target.checked }))} className="w-4 h-4 accent-pink-500" /> attivo</label>
              </div>
              <label className="text-zinc-500 text-xs">⭐ regalo (1 volta/anno nel giorno di nascita)<input type="number" data-testid="plus-bday-stars" value={settings.birthday_stars} onChange={(e) => setSettings((s) => ({ ...s, birthday_stars: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700" /></label>
            </div>
          </div>
          <button data-testid="plus-save-bonus" onClick={saveSettings} disabled={saving} className="mt-5 flex items-center gap-2 px-6 h-12 rounded-2xl bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Salva bonus
          </button>
        </div>

        {/* Customer Value Engine config + dashboard */}
        <div className="rounded-3xl bg-zinc-900 border border-zinc-800 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><Gauge className="w-5 h-5 text-sky-400" /><h2 className="font-display font-bold text-lg">Customer Value Engine <span className="text-xs text-zinc-500">(gestionale interno)</span></h2></div>
            <button data-testid="plus-cv-recompute" onClick={recomputeCV} disabled={saving} className="flex items-center gap-1 px-4 h-10 rounded-xl bg-sky-500/20 border border-sky-400/40 text-sky-300 text-sm disabled:opacity-60"><RefreshCw className="w-4 h-4" /> Ricalcola tutti</button>
          </div>

          {cvDash && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5" data-testid="plus-cv-dashboard">
              {(cvDash.classes || []).map((c) => (
                <div key={c.level} className="rounded-xl bg-zinc-800/50 border px-3 py-2 text-center" style={{ borderColor: `${c.colore}55` }}>
                  <div className="text-lg font-bold" style={{ color: c.colore }}>{cvDash.distribution?.[c.level] ?? 0}</div>
                  <div className="text-[10px] uppercase text-zinc-500">{c.level}</div>
                </div>
              ))}
            </div>
          )}

          <label className="block text-sm text-zinc-400 mb-2">Pesi componenti (%)</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {Object.keys(settings.cv_weights || {}).map((k) => (
              <div key={k}>
                <div className="text-[10px] uppercase text-zinc-500 mb-1">{k}</div>
                <input type="number" data-testid={`plus-cvw-${k}`} value={settings.cv_weights[k]}
                  onChange={(e) => setSettings((s) => ({ ...s, cv_weights: { ...s.cv_weights, [k]: Number(e.target.value) } }))}
                  className="w-full h-10 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-sm focus:outline-none focus:border-sky-400" />
              </div>
            ))}
          </div>

          <label className="block text-sm text-zinc-400 mb-2">Soglie classi (0–100)</label>
          <div className="space-y-2 mb-4">
            {(settings.cv_classes || []).map((c, i) => (
              <div key={c.level} className="flex items-center gap-2">
                <input type="color" value={c.colore} onChange={(e) => setSettings((s) => { const a = [...s.cv_classes]; a[i] = { ...a[i], colore: e.target.value }; return { ...s, cv_classes: a }; })} className="w-8 h-8 rounded bg-transparent border-0" />
                <span className="w-24 text-sm font-semibold" style={{ color: c.colore }}>{c.level}</span>
                <span className="text-zinc-500 text-xs">min ≥</span>
                <input type="number" data-testid={`plus-cvc-${c.level}`} value={c.min} onChange={(e) => setSettings((s) => { const a = [...s.cv_classes]; a[i] = { ...a[i], min: Number(e.target.value) }; return { ...s, cv_classes: a }; })} className="w-24 h-10 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-sm" />
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Periodo valutazione (giorni)</label>
              <input type="number" data-testid="plus-cv-period" value={settings.cv_period_days} onChange={(e) => setSettings((s) => ({ ...s, cv_period_days: e.target.value }))} className="w-28 h-10 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-sm" />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 mt-4">
              <input type="checkbox" data-testid="plus-cv-operator-view" checked={!!settings.cv_operator_can_view} onChange={(e) => setSettings((s) => ({ ...s, cv_operator_can_view: e.target.checked }))} className="w-5 h-5 accent-sky-500" />
              L'operatore può vedere punteggio e dettaglio (non solo la classe)
            </label>
          </div>
          <p className="text-zinc-600 text-xs mt-3">Le classi Steel/Bronze/Silver/Gold/Platinum sono SOLO gestionali: non generano Punti Stella, premi o vantaggi.</p>
          <button data-testid="plus-save-cv" onClick={saveSettings} disabled={saving} className="mt-4 flex items-center gap-2 px-6 h-12 rounded-2xl bg-sky-500 hover:bg-sky-400 font-bold transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Salva Customer Value
          </button>
        </div>

        {/* Zone editor grafico */}
        <div className="rounded-3xl bg-zinc-900 border border-zinc-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><MapPin className="w-5 h-5 text-sky-400" /><h2 className="font-display font-bold text-lg">Zone della struttura</h2>{currentVenue && <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/15 border border-sky-400/30 text-sky-300" data-testid="admin-zone-venue">{currentVenue.nome}</span>}</div>
            <button data-testid="plus-add-zone" onClick={addZone} className="flex items-center gap-1 px-4 h-10 rounded-xl bg-sky-500/20 border border-sky-400/40 text-sky-300 text-sm"><Plus className="w-4 h-4" /> Aggiungi zona</button>
          </div>
          <p className="text-zinc-500 text-sm mb-3">Trascina le zone sulla mappa per posizionarle (salvataggio automatico). Deseleziona "conta" per escludere una zona dal calcolo della permanenza.</p>
          <div className="flex flex-col lg:flex-row gap-6">
            <svg ref={svgRef} width={MAP_W} height={MAP_H} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
              className="rounded-2xl bg-zinc-950 border border-zinc-700 shrink-0 touch-none" data-testid="plus-zone-map">
              <rect x="0" y="0" width={MAP_W} height={MAP_H} fill="url(#grid)" />
              <defs>
                <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M20 0 L0 0 0 20" fill="none" stroke="#1f2430" strokeWidth="1" />
                </pattern>
              </defs>
              {zones.map((z) => (
                <g key={z.id} onPointerDown={onPointerDown(z)} style={{ cursor: "grab" }} data-testid={`plus-zone-rect-${z.id}`}>
                  <rect x={z.x} y={z.y} width={z.w} height={z.h} rx="10" fill={`${z.colore}22`} stroke={z.colore} strokeWidth="2" />
                  <text x={z.x + 10} y={z.y + 24} fill={z.colore} fontSize="13" fontWeight="700">{z.nome}</text>
                </g>
              ))}
            </svg>
            <div className="flex-1 space-y-3">
              {zones.map((z) => (
                <div key={z.id} className="flex items-center gap-2 p-3 rounded-xl bg-zinc-800/50 border border-zinc-700">
                  <input type="color" value={z.colore} onChange={(e) => { updZoneField(z.id, "colore", e.target.value); }}
                    onBlur={() => patchZone(zones.find((x) => x.id === z.id))} className="w-8 h-8 rounded bg-transparent border-0" />
                  <input value={z.nome} data-testid={`plus-zone-name-${z.id}`} onChange={(e) => updZoneField(z.id, "nome", e.target.value)}
                    onBlur={() => patchZone(zones.find((x) => x.id === z.id))}
                    className="flex-1 h-10 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-sm focus:outline-none focus:border-sky-400" />
                  <label className="flex items-center gap-1 text-[11px] text-zinc-400 shrink-0" title="Conta come permanenza valida">
                    <input type="checkbox" data-testid={`plus-zone-counts-${z.id}`} checked={z.counts_for_presence !== false}
                      onChange={(e) => { updZoneField(z.id, "counts_for_presence", e.target.checked); patchZone({ ...zones.find((x) => x.id === z.id), counts_for_presence: e.target.checked }); }}
                      className="w-4 h-4 accent-sky-500" /> conta
                  </label>
                  <button data-testid={`plus-zone-del-${z.id}`} onClick={() => delZone(z.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Estrazione di Natale */}
        <div className="rounded-3xl bg-zinc-900 border border-zinc-800 p-6 mt-6">
          <div className="flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-amber-400" /><h2 className="font-display font-bold text-lg">Estrazione di Natale</h2></div>
          <p className="text-zinc-500 text-xs mb-4">Partecipano i clienti con almeno N visite valide nel periodo. Sorteggio casuale dei vincitori.</p>
          <form onSubmit={createRaffle} className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5 items-end">
            <label className="text-zinc-500 text-xs col-span-2 sm:col-span-1">Nome<input data-testid="plus-raffle-name" value={raffleForm.nome} onChange={(e) => setRaffleForm((s) => ({ ...s, nome: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700 text-sm" /></label>
            <label className="text-zinc-500 text-xs">Periodo (gg)<input type="number" data-testid="plus-raffle-days" value={raffleForm.days} onChange={(e) => setRaffleForm((s) => ({ ...s, days: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700 text-sm" /></label>
            <label className="text-zinc-500 text-xs">Min visite<input type="number" data-testid="plus-raffle-minvisits" value={raffleForm.min_valid_visits} onChange={(e) => setRaffleForm((s) => ({ ...s, min_valid_visits: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700 text-sm" /></label>
            <label className="text-zinc-500 text-xs">N° vincitori<input type="number" data-testid="plus-raffle-winners" value={raffleForm.num_winners} onChange={(e) => setRaffleForm((s) => ({ ...s, num_winners: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700 text-sm" /></label>
            <label className="text-zinc-500 text-xs">⭐ bonus vincitori<input type="number" data-testid="plus-raffle-bonus" value={raffleForm.bonus_stars} onChange={(e) => setRaffleForm((s) => ({ ...s, bonus_stars: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700 text-sm" /></label>
            <button data-testid="plus-raffle-create" type="submit" disabled={saving} className="h-10 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold text-sm col-span-2 sm:col-span-1 disabled:opacity-60">Crea estrazione</button>
          </form>
          <div className="space-y-2" data-testid="plus-raffle-list">
            {raffles.length === 0 ? <p className="text-zinc-500 text-sm">Nessuna estrazione.</p> : raffles.map((r) => (
              <div key={r.id} data-testid={`plus-raffle-${r.id}`} className="p-3 rounded-xl bg-zinc-800/50 border border-zinc-700">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold">{r.nome}</span>
                    <span className="ml-2 text-xs text-zinc-500">{r.participants_count} partecipanti · min {r.min_valid_visits} visite · {r.num_winners} vincitori</span>
                  </div>
                  {r.stato === "DRAWN" ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-green-500/15 text-green-400">Estratta</span>
                  ) : (
                    <button data-testid={`plus-raffle-draw-${r.id}`} onClick={() => drawRaffle(r.id)} disabled={saving} className="px-4 h-9 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold text-sm disabled:opacity-60">Sorteggia</button>
                  )}
                </div>
                {r.winners && r.winners.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {r.winners.map((w) => (
                      <span key={w.id} className="text-xs px-2 py-1 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-300">🏆 {w.nome} {w.cognome}{w.awarded ? ` · +${r.bonus_stars}⭐` : ""}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Catalogo Premi */}
        <div className="rounded-3xl bg-zinc-900 border border-zinc-800 p-6 mt-6" data-testid="plus-admin-catalog">
          <div className="flex items-center gap-2 mb-2"><Gift className="w-5 h-5 text-amber-400" /><h2 className="font-display font-bold text-lg">Catalogo Premi</h2></div>
          <p className="text-zinc-500 text-xs mb-5">Premi riscattabili con i Punti Stella. Nessun vincolo di classe Stamina (firewall): tutti riscattano con i punti.</p>

          {/* Form nuovo premio */}
          <form onSubmit={createReward} className="rounded-2xl bg-zinc-800/40 border border-zinc-700 p-4 mb-5">
            <div className="flex flex-col sm:flex-row gap-4">
              <button type="button" onClick={() => rewardFileRef.current?.click()} disabled={uploadingImg}
                className="w-28 h-28 shrink-0 rounded-xl bg-zinc-800 border border-dashed border-zinc-600 flex items-center justify-center overflow-hidden hover:border-amber-400/50 transition-colors">
                {uploadingImg ? <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                  : rewardForm.image_url ? <img src={imgUrl(rewardForm.image_url)} alt="premio" className="w-full h-full object-cover" />
                  : <span className="text-zinc-500 text-xs text-center px-2">+ Foto premio</span>}
              </button>
              <input ref={rewardFileRef} data-testid="plus-reward-image" type="file" accept="image/*" onChange={uploadRewardImage} className="hidden" />
              <div className="flex-1 grid grid-cols-2 gap-3">
                <label className="text-zinc-500 text-xs col-span-2">Nome premio<input data-testid="plus-reward-nome" value={rewardForm.nome} onChange={(e) => setRewardForm((s) => ({ ...s, nome: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white" /></label>
                <label className="text-zinc-500 text-xs col-span-2">Descrizione<input data-testid="plus-reward-desc" value={rewardForm.descrizione} onChange={(e) => setRewardForm((s) => ({ ...s, descrizione: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white" /></label>
                <label className="text-zinc-500 text-xs">Costo (Punti Stella)<input data-testid="plus-reward-points" type="number" value={rewardForm.points_required} onChange={(e) => setRewardForm((s) => ({ ...s, points_required: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white" /></label>
                <label className="text-zinc-500 text-xs">Stock (se attivo)<input data-testid="plus-reward-stock" type="number" value={rewardForm.stock_quantity} onChange={(e) => setRewardForm((s) => ({ ...s, stock_quantity: e.target.value }))} className="w-full h-10 px-3 mt-1 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white" /></label>
                <label className="flex items-center gap-2 text-xs text-zinc-400 col-span-2"><input type="checkbox" data-testid="plus-reward-stock-track" checked={rewardForm.stock_tracking} onChange={(e) => setRewardForm((s) => ({ ...s, stock_tracking: e.target.checked }))} className="w-4 h-4 accent-amber-500" /> Gestisci disponibilità (stock)</label>
              </div>
            </div>
            <button data-testid="plus-reward-create" type="submit" disabled={saving} className="mt-4 flex items-center gap-2 px-6 h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold text-sm transition-colors disabled:opacity-60">
              <Plus className="w-4 h-4" /> Aggiungi premio
            </button>
          </form>

          {/* Lista premi esistenti */}
          <div className="space-y-2" data-testid="plus-reward-list">
            {rewards.length === 0 ? <p className="text-zinc-500 text-sm">Nessun premio nel catalogo.</p> : rewards.map((r) => (
              <div key={r.id} data-testid={`plus-reward-${r.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/50 border border-zinc-700">
                <div className="w-12 h-12 rounded-lg bg-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
                  {r.image_url ? <img src={imgUrl(r.image_url)} alt={r.nome} className="w-full h-full object-cover" /> : <Gift className="w-5 h-5 text-zinc-600" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{r.nome}</div>
                  {r.descrizione && <div className="text-zinc-500 text-xs truncate">{r.descrizione}</div>}
                </div>
                <label className="text-zinc-500 text-[10px] shrink-0">Punti<input type="number" defaultValue={r.points_required} data-testid={`plus-reward-points-${r.id}`}
                  onBlur={(e) => { const v = Number(e.target.value); if (v > 0 && v !== r.points_required) patchReward(r.id, { points_required: v }); }}
                  className="w-20 h-9 px-2 mt-0.5 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-amber-400 font-semibold" /></label>
                {r.stock_tracking && (
                  <label className="text-zinc-500 text-[10px] shrink-0">Stock<input type="number" defaultValue={r.stock_quantity} data-testid={`plus-reward-stockq-${r.id}`}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== r.stock_quantity) patchReward(r.id, { stock_quantity: v }); }}
                    className="w-16 h-9 px-2 mt-0.5 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-zinc-300" /></label>
                )}
                <label className="flex items-center gap-1 text-[11px] text-zinc-400 shrink-0" title="Visibile nel catalogo">
                  <input type="checkbox" data-testid={`plus-reward-active-${r.id}`} checked={!!r.attivo} onChange={(e) => patchReward(r.id, { attivo: e.target.checked })} className="w-4 h-4 accent-amber-500" /> attivo
                </label>
                <button data-testid={`plus-reward-del-${r.id}`} onClick={() => deleteReward(r.id)} className="text-red-400 hover:text-red-300 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
